import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../../../app/hooks';
import { useBoundedPoll } from '../../../../../common/hooks/useBoundedPoll';
import { GRACE_WINDOW_MS, useGraceWindow } from '../../../../../common/hooks/useGraceWindow';
import { useLatestRequestGuard } from '../../../../../common/hooks/useLatestRequestGuard';
import usePrevious from '../../../../../common/_custom/Hooks/usePrevious';
import { isTerminalFailure, TASK_TRACKER_STATUS } from '../../../../../utils/constants/taskTrackerStatus';
import { AION_INGEST_LITIGATION_CLASSIFICATION_DATA } from '../../../DashboardModules/Overview/components/ManagementProfile/components/managementUserProfileUtils';
import { getAllTaskTrackerData } from '../../../../../store/investor/investorApiHelper';
import type { StepProgress } from '../../../../../store/investor/taskTrackerResolution';
import { DATA_FETCH_STATUS } from '../../../../../store/dashboard/constants';
import { TabModule } from '../../../../../utils/constants/commonConstants';
import {
    LEGAL_CASES_GRACE_ATTEMPTS,
    LEGAL_CASES_GROUPED_ATTEMPTS,
    LEGAL_CASES_PENDING_ATTEMPTS,
    LEGAL_CASES_POLL_INTERVAL_MS,
} from './legalCasesConstants';

type PollingPhase = 'pending' | 'grace';

interface LegalCasesPollingResult {
    isPolling: boolean;
    isFetchFailed: boolean;
}

/**
 * Owns the litigation-tracker polling ladder for the Legal Cases tab:
 *   - PDF report route (`isPDFReport`) -> never polls, never shows the loader. `TabSelector`
 *     doesn't mount there, so the redux tracker map is always empty/stale for that render — a
 *     print surface must render whatever it has immediately, not enter the ladder.
 *   - FAILED/FAILURE tracker status -> stop polling, no loader. Shows the failure indicator only
 *     when there are no case rows to fall back on; with last-known rows the header just goes quiet.
 *   - TODO/TODO_ASYNC/absent -> keep polling on a 5s cadence, show the loader.
 *   - `inProgress` on the tracker read (per-person rows still open) -> keep polling past the ladder,
 *     bounded by LEGAL_CASES_GROUPED_ATTEMPTS, refetch once when it turns false, then stop (no grace window).
 *   - COMPLETE -> render rows if present, and keep polling for one more GRACE_WINDOW_MS window
 *     (armed once per run — org and refresh — and re-armed on an org switch or a refresh) so
 *     rows landing moments after COMPLETE aren't missed. The indicator stays up for the whole
 *     window regardless of whether rows are already present — the table itself always renders,
 *     so there is nothing left for the loader to gate.
 *
 * Before the BE fix ships, litigation rows can sit TODO_ASYNC forever — the pending poll's own
 * attempt budget is what eventually stops the loader in that case (not just the network calls).
 *
 * The redux tracker map is a one-shot snapshot taken at page load (see TabSelector.tsx), so a run
 * that completes mid-page would otherwise go unnoticed until the pending budget itself runs out.
 * `refreshTracker` re-reads the tracker on every pending poll tick to close that gap — see its own
 * doc below for why it's hook-local instead of dispatched to redux.
 *
 * The redux snapshot's `status === 'Processing'` is the live "run in flight" signal while the
 * snapshot (and this hook's own re-read) may still read COMPLETE — polus never sends 'Processed',
 * so only 'Processing' is meaningful, and treating it as pending alongside the tracker avoids
 * arming the grace window early.
 *
 * `initialiseHeadlinePanelPolling` starts an unbounded headline-panel interval, so it is only
 * triggered on the live run signal (`legalCasesStatus === Processing`), once per transition into
 * that state — not on every tracker-derived pending render (TODO_ASYNC/absent included).
 */
export function useLegalCasesPolling({
    assesseeOrgId,
    legalCasesStatus,
    hasResponse,
    hasCaseRows,
    fetchData,
    initialiseHeadlinePanelPolling,
    isPDFReport = false,
}: {
    assesseeOrgId: string;
    legalCasesStatus?: string;
    hasResponse: boolean;
    hasCaseRows: boolean;
    fetchData: (isPollingCall: boolean) => void;
    initialiseHeadlinePanelPolling?: Function;
    isPDFReport?: boolean;
}): LegalCasesPollingResult {
    const reduxTrackerStatus = useAppSelector(
        (state) => state.investor.taskTrackerMap?.[AION_INGEST_LITIGATION_CLASSIFICATION_DATA]?.status,
    );
    const isLegalRefreshRunning = useAppSelector((state) => state.investor.modulePollingList?.includes(TabModule.LEGAL_CHECKS) ?? false);

    // Every refresh starts a new run: all hook-local state below is keyed by it, so no budget or stop carries over.
    const [refreshCount, setRefreshCount] = useState(0);
    const wasRefreshRunningRef = useRef(isLegalRefreshRunning);
    // A tracked run ends at its settle refetch — the grace window is only the legacy ladder's wait for late rows.
    const [settledRunKey, setSettledRunKey] = useState('');
    const wasInProgressRef = useRef(false);
    // Hook-local, run-stamped re-read of the tracker (see refreshTracker below). Falls back to
    // the redux snapshot until THIS run's own tracker call has resolved — preserving the correct
    // FAILED classification before any tick, and dropping a stale org's status on a switch.
    const [tracker, setTracker] = useState<{ runKey: string; status?: string; inProgress?: boolean; completed?: number }>({ runKey: '' });

    useEffect(() => {
        if (isLegalRefreshRunning && !wasRefreshRunningRef.current) setRefreshCount((count) => count + 1);
        wasRefreshRunningRef.current = isLegalRefreshRunning;
    }, [isLegalRefreshRunning]);
    const runKey = `${assesseeOrgId}:${refreshCount}`;

    const isCurrentRun = tracker.runKey === runKey;
    // Undefined on any render outside the current run, so a run's first read is a baseline; a read with no per-person rows yet counts 0.
    const previousCompleted = usePrevious(isCurrentRun ? tracker.completed ?? 0 : undefined);
    const litigationTrackerStatus = isCurrentRun ? tracker.status : reduxTrackerStatus;
    const inProgress = !isPDFReport && isCurrentRun && tracker.inProgress === true;

    const isTerminal = isTerminalFailure(litigationTrackerStatus);
    const isComplete =
        hasResponse && litigationTrackerStatus === TASK_TRACKER_STATUS.COMPLETE && legalCasesStatus !== DATA_FETCH_STATUS.PROCESSING;
    const phase: PollingPhase = isComplete ? 'grace' : 'pending';

    // Set once the pending poll's own attempt budget is spent without ever reaching COMPLETE —
    // the loader must not spin forever on a step that never resolves.
    const [pendingCapReached, setPendingCapReached] = useState(false);

    // Resets the pending-cap flag on a genuine org switch or refresh — a stale cap flag from the previous
    // run must not leak into the next one. The grace window itself is reset internally by
    // useGraceWindow, keyed on the same runKey.
    useEffect(() => {
        setPendingCapReached(false);
    }, [runKey]);

    // Arms the window exactly once per (runKey, COMPLETE) pair — a later re-render that still reads
    // COMPLETE for the SAME run (e.g. a modulePollingList identity flip) must not restart the
    // countdown, but an org switch or a refresh re-arms a fresh window.
    const { windowExpired } = useGraceWindow({
        isComplete,
        resetKey: runKey,
        windowMs: GRACE_WINDOW_MS,
        enabled: !isPDFReport && !inProgress,
        onArm: () => setPendingCapReached(false),
    });

    // Fires the headline-panel poll only on the live "run in flight" signal, once per transition
    // into it — not on every tracker-derived pending render (TODO_ASYNC/absent included).
    const headlinePanelTriggeredRef = useRef(false);
    useEffect(() => {
        const isLive = !isPDFReport && legalCasesStatus === DATA_FETCH_STATUS.PROCESSING;
        if (isLive && !headlinePanelTriggeredRef.current) {
            headlinePanelTriggeredRef.current = true;
            initialiseHeadlinePanelPolling?.();
        } else if (!isLive) {
            headlinePanelTriggeredRef.current = false;
        }
    }, [legalCasesStatus, initialiseHeadlinePanelPolling, isPDFReport]);

    const isSettled = settledRunKey === runKey;

    const shouldPoll = !isPDFReport && !isSettled && (inProgress || (!isTerminal && (!isComplete || !windowExpired)));

    // Re-reads the litigation tracker on every pending poll tick so a run that completes mid-page
    // is noticed within one tick, instead of only once the pending poll's own budget runs out.
    // Hook-local rather than dispatched to redux: taskTrackerMap is TabSelector-owned and read by
    // unrelated gates (management-profile spinner, modulePollingList sweeps, its own org-switch
    // reset) — writing it from here would race TabSelector's own writes and silently widen this
    // change's blast radius past Legal Cases.
    const trackerRequestGuard = useLatestRequestGuard();
    const refreshTracker = () => {
        const token = trackerRequestGuard.startRequest();
        const requestRunKey = runKey;
        getAllTaskTrackerData(
            { orgId: assesseeOrgId },
            {
                onSuccess: (taskTrackerData, stepProgress?: StepProgress) => {
                    if (!trackerRequestGuard.isCurrent(token)) return;
                    const status = taskTrackerData?.[AION_INGEST_LITIGATION_CLASSIFICATION_DATA]?.status;
                    const litigationStepProgress = stepProgress?.[AION_INGEST_LITIGATION_CLASSIFICATION_DATA];
                    const readInProgress = litigationStepProgress?.inProgress;
                    const readCompleted = litigationStepProgress?.completed;
                    setTracker((prev) =>
                        prev.runKey === requestRunKey &&
                        prev.status === status &&
                        prev.inProgress === readInProgress &&
                        prev.completed === readCompleted
                            ? prev
                            : { runKey: requestRunKey, status, inProgress: readInProgress, completed: readCompleted },
                    );
                },
                // Keep the last known status on error — the redux snapshot remains the floor.
                onError: () => {},
            },
            true, // noToast: a pending poll's own tracker re-read must never toast (A-35)
        );
    };

    // While per-person rows are open only the tracker is read; the settle effect below fetches once.
    const pollTick = (isPollingCall: boolean) => {
        if (!inProgress) fetchData(isPollingCall);
        if ((phase === 'pending' && !isTerminal) || inProgress) refreshTracker();
    };

    const phaseAttempts = phase === 'pending' ? LEGAL_CASES_PENDING_ATTEMPTS : LEGAL_CASES_GRACE_ATTEMPTS;
    useBoundedPoll(pollTick, shouldPoll, () => setPendingCapReached(true), {
        intervalMs: LEGAL_CASES_POLL_INTERVAL_MS,
        maxAttempts: inProgress ? LEGAL_CASES_GROUPED_ATTEMPTS : phaseAttempts,
        fireImmediately: false,
        stickyAttemptsKey: `${runKey}:${inProgress ? 'grouped' : phase}`,
    });

    const isPolling = shouldPoll && !pendingCapReached;

    // Budget-spent is deliberately excluded — the tracker never reported FAILED there, so it is
    // not a failure, just an abandoned wait. PDF route never shows the indicator at all. With
    // last-known rows already on screen the header goes quiet instead of showing the indicator —
    // only a terminal failure with nothing to show earns the "Not able to fetch" treatment. Never
    // shown alongside the loader (`inProgress` keeps polling past a company-level FAILED).
    const isFetchFailed = !isPDFReport && isTerminal && !hasCaseRows && !isPolling;

    // One tracker read per org open and per refresh, so a run still in progress is seen without waiting for a tick.
    useEffect(() => {
        if (!isPDFReport) refreshTracker();
    }, [runKey, isPDFReport]);

    useEffect(() => {
        if (wasInProgressRef.current && !inProgress && isCurrentRun) {
            fetchData(true);
            setSettledRunKey(runKey);
        }
        wasInProgressRef.current = inProgress;
    }, [inProgress, isCurrentRun, fetchData, runKey]);

    // Shows each per-person row's cases as it lands.
    useEffect(() => {
        if (
            inProgress &&
            typeof previousCompleted === 'number' &&
            typeof tracker.completed === 'number' &&
            tracker.completed > previousCompleted
        ) {
            fetchData(true);
        }
    }, [tracker.completed, previousCompleted, inProgress, fetchData]);

    return { isPolling, isFetchFailed };
}
